/**
 * Pure channel-resolution logic extracted from utm-tracker-client.tsx.
 * No side-effects, no browser APIs — safe to import in tests and server code.
 */

export interface UtmObject {
  utm_source: string | null | undefined
  utm_medium: string | null | undefined
  utm_campaign: string | null | undefined
  utm_content?: string | null
  utm_term?: string | null
}

export interface ResolveChannelInput {
  /** window.document.referrer — empty string or full URL */
  referrer: string
  utm_source?: string | null
  utm_medium?: string | null
  utm_campaign?: string | null
  utm_term?: string | null
  utm_content?: string | null
}

/**
 * Resolves the UTM attribution object from a referrer URL and/or explicit UTM
 * params.  Explicit UTM params always win over the referrer fallback.
 *
 * Returns `null` for direct / self-referral visits (no signal at all).
 */
export function resolveChannelFromReferrerAndUtm(input: ResolveChannelInput): UtmObject | null {
  const { referrer, utm_source, utm_medium, utm_campaign, utm_term, utm_content } = input

  let utm: UtmObject | undefined

  // Referrer-based fallback (same order as the original component)
  if (referrer?.includes('tiktok')) {
    utm = { utm_source: 'tiktok', utm_medium: 'social', utm_campaign: 'bio' }
  } else if (referrer?.includes('instagram') || referrer?.includes('facebook')) {
    utm = { utm_source: 'facebook', utm_medium: 'ads', utm_campaign: 'bio' }
  } else if (referrer?.includes('snapchat')) {
    utm = {
      utm_source: 'snapchat',
      utm_medium: 'social',
      utm_campaign: 'bio',
    }
  } else if (referrer?.includes('google')) {
    utm = { utm_source: 'google', utm_medium: 'search', utm_campaign: undefined }
  } else if (referrer?.includes('bing')) {
    utm = { utm_source: 'bing', utm_medium: 'search', utm_campaign: undefined }
  } else if (referrer?.includes('duckduckgo')) {
    utm = { utm_source: 'duckduckgo', utm_medium: 'search', utm_campaign: undefined }
  } else if (referrer?.includes('ecosia')) {
    utm = { utm_source: 'ecosia', utm_medium: 'search', utm_campaign: undefined }
  } else if (referrer?.includes('search.brave')) {
    utm = { utm_source: 'brave', utm_medium: 'search', utm_campaign: undefined }
  } else if (referrer && !referrer.includes('roast')) {
    const domain = referrer
      .replace('https://', '')
      .replace('http://', '')
      .replace('www.', '')
      .split(/[/?#]/)[0]
    utm = { utm_source: domain, utm_medium: 'referral', utm_campaign: undefined }
  }

  // Explicit UTM params always override referrer heuristics
  if (utm_source || utm_medium || utm_campaign || utm_term || utm_content) {
    if (utm_campaign === 'client_share') {
      utm = {
        utm_source: 'tiktok',
        utm_medium: 'ads',
        utm_campaign,
        utm_content,
        utm_term,
      }
    } else {
      utm = { utm_source, utm_medium, utm_campaign, utm_content, utm_term }
    }
  }

  return utm ?? null
}
